import React from "react";
import {Alert, Badge, Card, Col, Form, Row} from "react-bootstrap";
import {RadioList} from "../common/RadioList";
import {Glyph} from "../common/Glyph";

const CATEGORY_LABELS = {
    LIEN: 'Lien / Injury',
    WORKERS_COMP: 'Workers Comp'
};

// Column widths from lien_data_sheet (plus paralegal fields, which land on lien_case).
// text/date columns are omitted.
const LIEN_FIELD_MAX_LENGTHS = {
    typeOfInjury: 45,
    thirdPartyPolicyLimits: 100,
    medpay: 1,
    medpayInfo: 100,
    uim: 1,
    uimLimits: 45,
    priorAccidents2y: 1,
    priorAccidentDates: 100,
    priorCaseSettled: 1,
    priorNeuroCare: 1,
    priorNeuroCareWho: 100,
    priorNeuroCareWhen: 45,
    adverseDriverName: 100,
    adverseDriverDob: 45,
    referredFor: 45,
    servicesAuthorized: 1,
    lien: 45,
    insurance: 1,
    policeReport: 1,
    policeReportWillSend: 1,
    pharmacy: 45,
    approvalStatus: 45,
    paralegalName: 100,
    paralegalEmail: 100
};

// Column widths from wc_data_sheet; text/date columns are omitted.
const WC_FIELD_MAX_LENGTHS = {
    referralDate: 45,
    claimNumber: 100,
    employer: 255,
    approvedDx: 255,
    tpaName: 255,
    adjusterName: 100,
    adjusterPhone: 45,
    adjusterFax: 45,
    nurseCaseManagerName: 100,
    nurseCaseManagerCompany: 255,
    nurseCaseManagerPhone: 45,
    nurseCaseManagerFax: 45,
    attorneyName: 100,
    attorneyPhone: 45,
    attorneyFax: 45,
    servicesRequested: 100,
    testingType: 255,
    primaryMdName: 255,
    priorNeuroCare: 1,
    priorNeuroName: 100,
    approvalStatus: 45
};

export default class ReferralUploadSpecialtySection extends React.Component {
    injuryTypes = ['MVA', 'PED vs MV', 'Slip & Fall'];

    fieldChanged = (section, e) => {
        const fname = e.target.name;
        const val = e.target.value;
        this.props.onChange(section, fname, val);
    };

    radioChanged = (section, name, val) => {
        this.props.onChange(section, name, val);
    };

    renderLienFields(d) {
        return <>
            <Form.Row>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Date of Injury</Form.Label>
                    <Form.Control name="dateOfInjury" value={d.dateOfInjury || ''} onChange={e => this.fieldChanged('lienDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Type of Injury</Form.Label>
                    <Form.Control as="select" custom name="typeOfInjury" value={d.typeOfInjury || ''} onChange={e => this.fieldChanged('lienDetails', e)}>
                        <option value=""> </option>
                        {[...this.injuryTypes, 'Other'].map(v => <option key={v} value={v}>{v}</option>)}
                    </Form.Control>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Referred For</Form.Label>
                    <Form.Control as="select" custom name="referredFor" value={d.referredFor || ''} onChange={e => this.fieldChanged('lienDetails', e)}>
                        {['', 'Consult', 'Testing Only', 'Consult and Testing'].map(v => <option key={v} value={v}>{v}</option>)}
                    </Form.Control>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={12}>
                    <Form.Label>Description of Accident</Form.Label>
                    <Form.Control name="descriptionOfAccident" value={d.descriptionOfAccident || ''} onChange={e => this.fieldChanged('lienDetails', e)}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>3rd Party Policy Limits</Form.Label>
                    <Form.Control name="thirdPartyPolicyLimits" maxLength={LIEN_FIELD_MAX_LENGTHS.thirdPartyPolicyLimits}
                                  value={d.thirdPartyPolicyLimits || ''} onChange={e => this.fieldChanged('lienDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>MedPay?</Form.Label>
                    <RadioList inline name="medpay" values={['Y', 'N', 'U']} labels={['Y', 'N', 'Unknown']}
                               currentValue={d.medpay} onChange={e => this.radioChanged('lienDetails', 'medpay', e.target.value)}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>UIM?</Form.Label>
                    <RadioList inline name="uim" values={['Y', 'N', 'U']} labels={['Y', 'N', 'Unknown']}
                               currentValue={d.uim} onChange={e => this.radioChanged('lienDetails', 'uim', e.target.value)}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={6}>
                    <Form.Label>Adverse Driver Name</Form.Label>
                    <Form.Control name="adverseDriverName" maxLength={LIEN_FIELD_MAX_LENGTHS.adverseDriverName}
                                  value={d.adverseDriverName || ''} onChange={e => this.fieldChanged('lienDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={6}>
                    <Form.Label>Paralegal / Case Manager</Form.Label>
                    <Form.Control name="paralegalName" maxLength={LIEN_FIELD_MAX_LENGTHS.paralegalName}
                                  value={d.paralegalName || ''} onChange={e => this.fieldChanged('lienDetails', e)}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Approval Status</Form.Label>
                    <RadioList inline name="approvalStatus" values={['Approved', 'Denied']} currentValue={d.approvalStatus}
                               onChange={e => this.radioChanged('lienDetails', 'approvalStatus', e.target.value)}/>
                </Form.Group>
            </Form.Row>
        </>;
    }

    renderWcFields(d) {
        return <>
            <Form.Row>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Date of Injury</Form.Label>
                    <Form.Control name="dateOfInjury" value={d.dateOfInjury || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Claim #</Form.Label>
                    <Form.Control name="claimNumber" maxLength={WC_FIELD_MAX_LENGTHS.claimNumber}
                                  value={d.claimNumber || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Employer</Form.Label>
                    <Form.Control name="employer" maxLength={WC_FIELD_MAX_LENGTHS.employer}
                                  value={d.employer || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={6}>
                    <Form.Label>TPA Name</Form.Label>
                    <Form.Control name="tpaName" maxLength={WC_FIELD_MAX_LENGTHS.tpaName}
                                  value={d.tpaName || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={6}>
                    <Form.Label>Approved DX</Form.Label>
                    <Form.Control name="approvedDx" maxLength={WC_FIELD_MAX_LENGTHS.approvedDx}
                                  value={d.approvedDx || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Adjuster Name</Form.Label>
                    <Form.Control name="adjusterName" maxLength={WC_FIELD_MAX_LENGTHS.adjusterName}
                                  value={d.adjusterName || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Adjuster Phone</Form.Label>
                    <Form.Control name="adjusterPhone" maxLength={WC_FIELD_MAX_LENGTHS.adjusterPhone}
                                  value={d.adjusterPhone || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Adjuster Fax</Form.Label>
                    <Form.Control name="adjusterFax" maxLength={WC_FIELD_MAX_LENGTHS.adjusterFax}
                                  value={d.adjusterFax || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={6}>
                    <Form.Label>Nurse Case Manager</Form.Label>
                    <Form.Control name="nurseCaseManagerName" maxLength={WC_FIELD_MAX_LENGTHS.nurseCaseManagerName}
                                  value={d.nurseCaseManagerName || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={6}>
                    <Form.Label>NCM Company</Form.Label>
                    <Form.Control name="nurseCaseManagerCompany" maxLength={WC_FIELD_MAX_LENGTHS.nurseCaseManagerCompany}
                                  value={d.nurseCaseManagerCompany || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Attorney Name</Form.Label>
                    <Form.Control name="attorneyName" maxLength={WC_FIELD_MAX_LENGTHS.attorneyName}
                                  value={d.attorneyName || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Attorney Phone</Form.Label>
                    <Form.Control name="attorneyPhone" maxLength={WC_FIELD_MAX_LENGTHS.attorneyPhone}
                                  value={d.attorneyPhone || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label>Attorney Fax</Form.Label>
                    <Form.Control name="attorneyFax" maxLength={WC_FIELD_MAX_LENGTHS.attorneyFax}
                                  value={d.attorneyFax || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={6}>
                    <Form.Label>Services Requested</Form.Label>
                    <Form.Control name="servicesRequested" maxLength={WC_FIELD_MAX_LENGTHS.servicesRequested}
                                  value={d.servicesRequested || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={6}>
                    <Form.Label>Testing Type</Form.Label>
                    <Form.Control name="testingType" maxLength={WC_FIELD_MAX_LENGTHS.testingType}
                                  value={d.testingType || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={6}>
                    <Form.Label>Primary MD</Form.Label>
                    <Form.Control name="primaryMdName" maxLength={WC_FIELD_MAX_LENGTHS.primaryMdName}
                                  value={d.primaryMdName || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
                <Form.Group as={Col} sm={3}>
                    <Form.Label className="d-block">Prior Neuro?</Form.Label>
                    <RadioList className="d-block" name="priorNeuroCare" values={['Y', 'N']} currentValue={d.priorNeuroCare}
                               onChange={e => this.radioChanged('workersCompDetails', 'priorNeuroCare', e.target.value)}/>
                </Form.Group>
                <Form.Group as={Col} sm={3}>
                    <Form.Label className="d-block">Approval</Form.Label>
                    <RadioList className="d-block" name="approvalStatus" values={['Approved', 'Denied']} currentValue={d.approvalStatus}
                               onChange={e => this.radioChanged('workersCompDetails', 'approvalStatus', e.target.value)}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={12}>
                    <Form.Label>Referral Reason</Form.Label>
                    <Form.Control as="textarea" rows={2} name="referralReason" value={d.referralReason || ''} onChange={e => this.fieldChanged('workersCompDetails', e)}/>
                </Form.Group>
            </Form.Row>
        </>;
    }

    undoClicked = (e) => {
        e.preventDefault();
        this.props.onUndo();
    };

    render() {
        const category = this.props.referralCategory;
        if (!category || category === 'STANDARD') {
            return null;
        }

        if (this.props.ignored) {
            return <Alert variant="warning" className="mt-4 mb-2">
                Specialty referral info removed
                <a href="#" className="ml-2" onClick={this.undoClicked}>Undo</a>
            </Alert>;
        }

        const lienDetails = this.props.lienDetails || {};
        const workersCompDetails = this.props.workersCompDetails || {};

        return <Card className="mt-4 mb-2">
            <Card.Header>
                <Row className="align-items-center">
                    <Col>
                        Specialty Referral
                        <Badge variant="info" className="ml-2">{CATEGORY_LABELS[category] || category}</Badge>
                    </Col>
                    <Col xs="auto">
                        <Glyph name="trash" variant="danger" className="clickable"
                               label="Remove specialty referral info" onClick={this.props.onIgnore}/>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                {category === 'LIEN' && this.renderLienFields(lienDetails)}
                {category === 'WORKERS_COMP' && this.renderWcFields(workersCompDetails)}
            </Card.Body>
        </Card>;
    }
}

export function visitSubtypeForCategory(category) {
    if (category === 'LIEN') return 'LV';
    if (category === 'WORKERS_COMP') return 'WC';
    return 'RV';
}

export function maxLengthsForCategory(category) {
    if (category === 'LIEN') return LIEN_FIELD_MAX_LENGTHS;
    if (category === 'WORKERS_COMP') return WC_FIELD_MAX_LENGTHS;
    return null;
}

export function limitSpecialtyDetails(category, details) {
    const maxLengths = maxLengthsForCategory(category);
    if (!details || !maxLengths) {
        return details || {};
    }

    const limited = {};
    for (const [fname, val] of Object.entries(details)) {
        const max = maxLengths[fname];
        limited[fname] = max && typeof val === 'string' ? val.trim().substring(0, max) : val;
    }
    return limited;
}
